import { useEffect, useState, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, Avatar, Stack, Divider, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, FormControlLabel, Checkbox, Alert, CircularProgress,
  InputAdornment, Tab, Tabs, TablePagination,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon, Edit as EditIcon, People as PeopleIcon,
  LockReset as ResetIcon, ToggleOn as ActivateIcon, ToggleOff as DeactivateIcon,
  Search as SearchIcon, AdminPanelSettings as ShieldIcon,
  CheckCircle as ActiveIcon, Block as InactiveIcon,
  Mail as MailIcon, Phone as PhoneIcon, Person as PersonIcon,
  HowToReg as HowToRegIcon, Close as CloseIcon,
  Badge as BadgeIcon, Apartment as DeptIcon, Wc as GenderIcon,
  AlternateEmail as UserIcon, Lock as LockIcon,
  Visibility as ShowIcon, VisibilityOff as HideIcon,
  AutoAwesome as MagicIcon, Casino as DiceIcon,
  Business as SchoolIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// Multi-school: `school_id` is left OUT of `blank` on purpose. When the
// field is absent the backend assigns the creating admin's own active
// school, which is the right default and keeps single-school installs
// behaving exactly as before. The School selector only adds the key
// once someone actually picks something.
const blank = {
  first_name: '', last_name: '', email: '', username: '', telephone: '',
  gender: '', department: '', password: '', role_id: '',
};

// Sentinel for "not tied to one school" — a super-user who can switch.
// Distinct from '' (meaning "leave as the default") because the backend
// treats an explicit null as the super-user marker.
const SUPER_USER = '__super__';

function StatTile({ icon, label, value, tone = 'primary', sub }) {
  return (
    <Card sx={{
      flex: 1, minWidth: 180, borderRadius: 2,
      borderLeft: (t) => `4px solid ${t.palette[tone].main}`,
      transition: 'transform .15s, box-shadow .15s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
    }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.8, py: 1.8 }}>
        <Box sx={{
          width: 46, height: 46, borderRadius: '50%',
          bgcolor: (t) => alpha(t.palette[tone].main, 0.14),
          color: `${tone}.main`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{
            textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 800, fontSize: 10, display: 'block',
          }}>{label}</Typography>
          <Typography variant="h5" fontWeight={800} lineHeight={1.15}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary" noWrap>{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function UsersPage() {
  const [users, setUsers]     = useState([]);
  const [roles, setRoles]     = useState([]);
  const [pages, setPages]     = useState([]);
  // Empty unless this deployment has multi-school on AND the caller
  // holds manage_schools. Gates the whole School selector.
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filterRole, setFilterRole]     = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage]       = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(blank);
  const [openDlg, setOpenDlg]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [tab, setTab]           = useState(0);
  const [pageOverrides, setPageOverrides] = useState([]);

  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw]     = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);

  const genPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let p = '';
    for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [u, r, p] = await Promise.all([
        API.get('/users'),
        API.get('/roles'),
        API.get('/roles/pages'),
      ]);
      setUsers(u.data); setRoles(r.data); setPages(p.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    } finally { setLoading(false); }

    // Multi-school: only reachable by someone holding manage_schools,
    // and 404s outright when the vendor flag is off. Failing quietly
    // is correct — the School selector simply doesn't render, and the
    // page works exactly as it did before multi-school existed.
    try {
      const s = await API.get('/schools');
      setSchools(s.data?.schools || []);
    } catch {
      setSchools([]);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null); setForm(blank); setPageOverrides([]); setError(''); setTab(0); setOpenDlg(true);
  };

  const openEdit = async (u) => {
    setError('');
    try {
      const res = await API.get(`/users/${u.id}`);
      const d = res.data;
      setEditing(u);
      setForm({
        first_name: d.first_name || '', last_name: d.last_name || '',
        email: d.email || '', username: d.username || '',
        telephone: d.telephone || '', gender: d.gender || '',
        department: d.department || '', password: '',
        role_id: d.role_id || '',
        // school_id NULL on the row means "super-user, may switch schools".
        school_id: d.school_id == null ? SUPER_USER : String(d.school_id),
      });
      setPageOverrides(d.page_overrides || []);
      setTab(0); setOpenDlg(true);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load user');
    }
  };

  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.email)
      { setError('First name, last name and email are required'); return; }
    if (!editing && (!form.password || form.password.length < 6))
      { setError('Password is required (6+ characters)'); return; }
    setSaving(true); setError('');

    // Multi-school: translate the selector into what the API expects.
    // Only include the key when a choice was actually made — the
    // backend leaves school_id untouched if the field is absent, which
    // stops the existing form from blanking someone's assignment.
    const schoolField = {};
    if (schools.length && form.school_id) {
      schoolField.school_id = form.school_id === SUPER_USER ? null : Number(form.school_id);
    }

    try {
      if (editing) {
        await API.put(`/users/${editing.id}`, {
          first_name: form.first_name, last_name: form.last_name,
          email: form.email, username: form.username,
          telephone: form.telephone, gender: form.gender,
          department: form.department, role_id: form.role_id || null,
          ...schoolField,
        });
        await API.put(`/users/${editing.id}/pages`, { pages: pageOverrides });
        setOpenDlg(false);
        swal.success(`${form.first_name} ${form.last_name} updated`);
      } else {
        await API.post('/users', { ...form, ...schoolField });
        const name = `${form.first_name} ${form.last_name}`;
        // Reset for the next user — keep department, role & school for
        // serial entry (you're usually adding a batch of staff to the
        // same school at once).
        setForm({
          ...blank,
          department: form.department,
          role_id: form.role_id,
          school_id: form.school_id,
        });
        setPageOverrides([]);
        swal.success(`${name} created — they'll change their password on first login. Add another or close.`);
      }
      await load();
    } catch (e) {
      setError(swal.pickError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const togglePageOverride = (key) => {
    setPageOverrides((prev) => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
  };

  const toggleStatus = async (u) => {
    const next = u.status === '1' ? '0' : '1';
    const action = next === '1' ? 'activate' : 'deactivate';
    const ok = await swal.confirm({
      title: `${action === 'activate' ? 'Activate' : 'Deactivate'} ${u.first_name} ${u.last_name}?`,
      text: action === 'deactivate'
        ? 'They will lose immediate access to the system until reactivated.'
        : 'They will be able to sign in again.',
      confirmText: action === 'activate' ? 'Activate' : 'Deactivate',
      tone: action === 'deactivate' ? 'warning' : 'success',
      icon: action === 'deactivate' ? 'warning' : 'question',
    });
    if (!ok) return;
    try {
      await API.post(`/users/${u.id}/status`, { status: next });
      swal.success(action === 'activate' ? 'User activated' : 'User deactivated');
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Update failed')); }
  };

  const submitReset = async () => {
    if (!resetPw || resetPw.length < 6) { swal.warning('Password must be at least 6 characters'); return; }
    try {
      await API.post(`/users/${resetUser.id}/reset-password`, { new_password: resetPw });
      setResetUser(null); setResetPw('');
      swal.success('Password reset — the user will be asked to change it on next login');
    } catch (e) { swal.error(swal.pickError(e, 'Reset failed')); }
  };

  const filtered = useMemo(() => users.filter(u => {
    const q = search.trim().toLowerCase();
    if (q) {
      const blob = `${u.first_name} ${u.last_name} ${u.email} ${u.username || ''}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    if (filterRole !== 'all') {
      if (filterRole === 'none' && u.role_id) return false;
      if (filterRole !== 'none' && String(u.role_id) !== String(filterRole)) return false;
    }
    if (filterStatus !== 'all' && u.status !== filterStatus) return false;
    return true;
  }), [users, search, filterRole, filterStatus]);

  const paged = filtered.slice(page * perPage, page * perPage + perPage);

  const stats = useMemo(() => ({
    total:    users.length,
    active:   users.filter(u => u.status === '1').length,
    inactive: users.filter(u => u.status !== '1').length,
    assigned: users.filter(u => !!u.role_id).length,
  }), [users]);

  const groupedPages = useMemo(() => pages.reduce((acc, p) => {
    acc[p.section] = acc[p.section] || []; acc[p.section].push(p); return acc;
  }, {}), [pages]);

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      {/* Header */}
      <Card sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', p: { xs: 2.5, md: 3 },
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 2,
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 52, height: 52 }}>
              <PeopleIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={800}>User Management</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Create staff accounts, assign roles and grant page access
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" color="inherit"
            startIcon={<AddIcon />} onClick={openCreate}
            sx={{ bgcolor: '#fff', color: 'primary.main', '&:hover': { bgcolor: '#f0f0f0' } }}>
            New User
          </Button>
        </Box>
      </Card>

      {/* Stats */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <StatTile icon={<PeopleIcon />}    label="Total Users"     value={stats.total}    tone="primary"
          sub={`${stats.assigned} with a role`} />
        <StatTile icon={<ActiveIcon />}    label="Active"          value={stats.active}   tone="success"
          sub={stats.total ? `${Math.round(stats.active * 100 / stats.total)}% of staff` : '—'} />
        <StatTile icon={<InactiveIcon />}  label="Inactive"        value={stats.inactive} tone="warning"
          sub={stats.inactive ? 'Cannot sign in' : 'All accounts active'} />
        <StatTile icon={<HowToRegIcon />}  label="Assigned a Role" value={stats.assigned} tone="secondary"
          sub={`${stats.total - stats.assigned} unassigned`} />
      </Stack>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search by name, email, username…"
            value={search} onChange={(e) => { setPage(0); setSearch(e.target.value); }}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Role" value={filterRole}
            onChange={(e) => { setPage(0); setFilterRole(e.target.value); }} sx={{ minWidth: 180 }}>
            <MenuItem value="all">All roles</MenuItem>
            <MenuItem value="none">No role assigned</MenuItem>
            <Divider />
            {roles.map(r => <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={filterStatus}
            onChange={(e) => { setPage(0); setFilterStatus(e.target.value); }} sx={{ minWidth: 140 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="0">Inactive</MenuItem>
          </TextField>
          {(search || filterRole !== 'all' || filterStatus !== 'all') && (
            <Button size="small" color="inherit" onClick={() => { setSearch(''); setFilterRole('all'); setFilterStatus('all'); setPage(0); }}
              startIcon={<CloseIcon fontSize="small" />}>
              Clear
            </Button>
          )}
        </Box>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
          <PeopleIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={800}>Staff Directory</Typography>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" variant="outlined"
            label={`${filtered.length} of ${users.length} user${users.length === 1 ? '' : 's'}`}
            sx={{ fontWeight: 700 }} />
        </Box>

        {error && !openDlg && (
          <Alert severity="error" sx={{ m: 2, borderRadius: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'grey.50',
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  borderBottom: 2,
                  borderColor: 'divider',
                },
                '& .MuiTableRow-root:not(:last-child) .MuiTableCell-body': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& .MuiTableRow-hover:hover': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Role</TableCell>
                    {schools.length > 1 && <TableCell>School</TableCell>}
                    <TableCell>Department</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map(u => {
                    const isSuper = u.role_name === 'super_admin';
                    const inactive = u.status !== '1';
                    return (
                      <TableRow key={u.id} hover sx={{ opacity: inactive ? 0.7 : 1 }}>
                        <TableCell sx={{ py: 1.4 }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{
                              width: 40, height: 40, fontSize: 15, fontWeight: 700,
                              bgcolor: isSuper ? 'secondary.main' : 'primary.main',
                              boxShadow: isSuper ? (t) => `0 0 0 3px ${alpha(t.palette.secondary.main, 0.2)}` : 'none',
                            }}>
                              {(u.first_name?.[0] || u.email?.[0])?.toUpperCase()}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Stack direction="row" spacing={0.6} alignItems="center">
                                <Typography variant="body2" fontWeight={700} noWrap>
                                  {u.first_name} {u.last_name}
                                </Typography>
                                {isSuper && (
                                  <Tooltip title="Super Administrator">
                                    <ShieldIcon sx={{ fontSize: 15, color: 'secondary.main' }} />
                                  </Tooltip>
                                )}
                                {/* The single account allowed to create and
                                    manage schools. Set by the first-run setup
                                    wizard; moves only via an explicit
                                    hand-over. */}
                                {u.is_owner === 1 && (
                                  <Tooltip title="Master owner — the only account that can create or manage schools">
                                    <Chip size="small" label="OWNER" color="warning"
                                      sx={{ height: 18, fontSize: 9.5, fontWeight: 800, ml: 0.3 }} />
                                  </Tooltip>
                                )}
                              </Stack>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {u.username ? `@${u.username}` : `ID #${u.id}`}
                                {u.gender ? ` · ${u.gender}` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.3}>
                            <Stack direction="row" spacing={0.7} alignItems="center">
                              <MailIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{u.email}</Typography>
                            </Stack>
                            {u.telephone ? (
                              <Stack direction="row" spacing={0.7} alignItems="center">
                                <PhoneIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                <Typography variant="caption" color="text.secondary">{u.telephone}</Typography>
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.disabled">No phone on file</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {u.role_label
                            ? <Chip size="small"
                                icon={isSuper ? <ShieldIcon sx={{ fontSize: 14 }} /> : undefined}
                                label={u.role_label}
                                color={isSuper ? 'secondary' : 'primary'}
                                variant={isSuper ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 700, height: 24 }} />
                            : <Chip size="small" label="No role" variant="outlined"
                                sx={{ fontWeight: 600, color: 'text.disabled', borderStyle: 'dashed', height: 24 }} />}
                        </TableCell>
                        {schools.length > 1 && (
                          <TableCell>
                            {u.school_id == null
                              ? <Chip size="small" label="All schools" color="warning"
                                  icon={<SchoolIcon sx={{ fontSize: 14 }} />}
                                  sx={{ fontWeight: 700, height: 24 }} />
                              : <Chip size="small" variant="outlined"
                                  label={u.school_name || `School #${u.school_id}`}
                                  sx={{ fontWeight: 600, height: 24 }} />}
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" color={u.department ? 'text.primary' : 'text.disabled'}>
                            {u.department || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small"
                            icon={u.status === '1' ? <ActiveIcon sx={{ fontSize: 14 }} /> : <InactiveIcon sx={{ fontSize: 14 }} />}
                            label={u.status === '1' ? 'Active' : 'Inactive'}
                            color={u.status === '1' ? 'success' : 'default'}
                            variant={u.status === '1' ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 700, height: 24,
                              ...(u.status !== '1' && { color: 'text.disabled', borderColor: 'grey.400' }) }} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.3} justifyContent="flex-end">
                            <Tooltip title="Edit user">
                              <IconButton size="small" onClick={() => openEdit(u)} color="primary"
                                sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset password">
                              <IconButton size="small" onClick={() => { setResetUser(u); setResetPw(''); }}
                                sx={{ color: 'warning.main', '&:hover': { bgcolor: (t) => alpha(t.palette.warning.main, 0.12) } }}>
                                <ResetIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={u.status === '1' ? 'Deactivate' : 'Activate'}>
                              <IconButton size="small" onClick={() => toggleStatus(u)}
                                color={u.status === '1' ? 'error' : 'success'}
                                sx={{ '&:hover': {
                                  bgcolor: (t) => alpha(u.status === '1' ? t.palette.error.main : t.palette.success.main, 0.12),
                                } }}>
                                {u.status === '1' ? <DeactivateIcon /> : <ActivateIcon />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={schools.length > 1 ? 7 : 6} align="center" sx={{ py: 8 }}>
                        <PeopleIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.disabled" fontWeight={700}>
                          {users.length === 0 ? 'No users yet' : 'No users match your filters'}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                          {users.length === 0
                            ? 'Click "New User" above to add your first staff account.'
                            : 'Try adjusting your search or clearing the filters.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div" count={filtered.length}
              page={page} onPageChange={(_, p) => setPage(p)}
              rowsPerPage={perPage} onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          </>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={openDlg} onClose={() => setOpenDlg(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        {/* Header */}
        <Box sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', px: 3, py: 2.2,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 46, height: 46, color: '#fff' }}>
            {editing
              ? ((form.first_name?.[0] || form.email?.[0] || '?').toUpperCase())
              : <AddIcon />}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              {editing ? `${editing.first_name} ${editing.last_name}` : 'Create New User'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {editing
                ? 'Update profile details, role, and per-user page access'
                : 'Add a new staff account to the school system'}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenDlg(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {editing && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50',
                  '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 44 } }}>
            <Tab label="Account Details" icon={<PersonIcon fontSize="small" />} iconPosition="start" />
            <Tab label="Page Access"     icon={<ShieldIcon fontSize="small" />} iconPosition="start" />
          </Tabs>
        )}

        <DialogContent dividers sx={{ bgcolor: 'grey.50', p: { xs: 2, md: 3 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {tab === 0 && (
            <Stack spacing={2}>
              {/* Personal */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <Box sx={{ px: 2.2, py: 1.2, borderBottom: 1, borderColor: 'divider',
                  display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                  <PersonIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={800}>Personal Information</Typography>
                </Box>
                <Box sx={{ p: 2.2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField size="small" label="First Name" value={form.first_name} required
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start"><BadgeIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                    ) }} />
                  <TextField size="small" label="Last Name" value={form.last_name} required
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start"><BadgeIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                    ) }} />
                  <TextField size="small" label="Phone" value={form.telephone}
                    onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start"><PhoneIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                    ) }} />
                  <TextField select size="small" label="Gender" value={form.gender}
                    onChange={(e) => setForm({ ...form, gender: e.target.value })}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start"><GenderIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                    ) }}>
                    <MenuItem value=""><em>Not specified</em></MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                  </TextField>
                </Box>
              </Card>

              {/* Login */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <Box sx={{ px: 2.2, py: 1.2, borderBottom: 1, borderColor: 'divider',
                  display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                  <LockIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={800}>Login Credentials</Typography>
                </Box>
                <Box sx={{ p: 2.2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField size="small" label="Email" type="email" value={form.email} required
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start"><MailIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                    ) }} />
                  <TextField size="small" label="Username" value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    helperText="Optional — for sign-in if email not used"
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start"><UserIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                    ) }} />
                  {!editing && (
                    <TextField size="small" label="Initial Password" required
                      type={showPw ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      helperText="Minimum 6 characters · user must change it on first login"
                      sx={{ gridColumn: { sm: 'span 2' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start"><LockIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Generate strong password">
                              <IconButton size="small" onClick={() => setForm(f => ({ ...f, password: genPassword() }))}>
                                <DiceIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={showPw ? 'Hide' : 'Show'}>
                              <IconButton size="small" onClick={() => setShowPw(s => !s)}>
                                {showPw ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }} />
                  )}
                </Box>
              </Card>

              {/* Workplace */}
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <Box sx={{ px: 2.2, py: 1.2, borderBottom: 1, borderColor: 'divider',
                  display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                  <ShieldIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={800}>
                    {schools.length > 1 ? 'Role, School & Department' : 'Role & Department'}
                  </Typography>
                </Box>
                <Box sx={{ p: 2.2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField select size="small" label="Role" value={form.role_id}
                    onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                    helperText="Determines page & report access"
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start"><ShieldIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                    ) }}>
                    <MenuItem value=""><em>No role</em></MenuItem>
                    {roles.map(r => <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label="Department" value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start"><DeptIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
                    ) }} />

                  {/* Multi-school only. Hidden entirely when this
                      deployment has one school, so the form is
                      unchanged for every ordinary install. */}
                  {schools.length > 1 && (
                    <TextField select size="small" label="School"
                      value={form.school_id ?? ''}
                      onChange={(e) => setForm({ ...form, school_id: e.target.value })}
                      helperText={
                        form.school_id === SUPER_USER
                          ? 'Can switch between all schools after signing in'
                          : 'The only school this person can see'
                      }
                      sx={{ gridColumn: { sm: '1 / -1' } }}
                      InputProps={{ startAdornment: (
                        <InputAdornment position="start">
                          <SchoolIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                      ) }}>
                      {!editing && <MenuItem value=""><em>Default — my current school</em></MenuItem>}
                      {schools.map(s => (
                        <MenuItem key={s.id} value={String(s.id)}>
                          {s.name} <em style={{ opacity: 0.6, marginLeft: 6 }}>({s.code})</em>
                        </MenuItem>
                      ))}
                      <MenuItem value={SUPER_USER}>
                        <strong>All schools</strong>
                        <em style={{ opacity: 0.6, marginLeft: 6 }}>— can switch</em>
                      </MenuItem>
                    </TextField>
                  )}
                </Box>
              </Card>

              {schools.length > 1 && form.school_id === SUPER_USER && (
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                  <strong>All schools</strong> lets this person read and change data in
                  every school from one login. Reserve it for owners and
                  administrators — ordinary staff should be pinned to a single school.
                </Alert>
              )}

              {!editing && (
                <Alert severity="info" variant="outlined" icon={<MagicIcon fontSize="small" />}
                  sx={{ borderRadius: 2 }}>
                  After creating, you can <strong>open the user again</strong> to grant per-user page access on top of their role.
                </Alert>
              )}
            </Stack>
          )}

          {tab === 1 && editing && (
            <Stack spacing={2}>
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                Pages selected here are granted to <strong>this user only</strong> — in addition to whatever their role grants.
              </Alert>
              {Object.entries(groupedPages).map(([section, items]) => {
                const onCount = items.filter(p => pageOverrides.includes(p.key)).length;
                return (
                  <Card key={section} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Box sx={{ px: 2.2, py: 1.2, borderBottom: 1, borderColor: 'divider',
                      display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                      <Typography variant="overline" color="primary" fontWeight={800}>{section}</Typography>
                      <Box sx={{ flex: 1 }} />
                      {onCount > 0 && (
                        <Chip size="small" color="primary" label={`${onCount}/${items.length}`} sx={{ fontWeight: 700, height: 22 }} />
                      )}
                    </Box>
                    <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                      {items.map(p => (
                        <FormControlLabel key={p.key}
                          sx={{ m: 0, px: 1, py: 0.4, borderRadius: 1,
                            '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.05) } }}
                          control={<Checkbox size="small" checked={pageOverrides.includes(p.key)} onChange={() => togglePageOverride(p.key)} />}
                          label={<Typography variant="body2">{p.label}</Typography>}
                        />
                      ))}
                    </Box>
                  </Card>
                );
              })}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.7, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ flex: 1, color: 'text.secondary', fontSize: 12 }}>
            {!editing ? 'You can grant page access after creating the user.' : ''}
          </Box>
          <Button onClick={() => setOpenDlg(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (editing ? <EditIcon /> : <AddIcon />)}
            sx={{ minWidth: 150, fontWeight: 700 }}>
            {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create User')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetUser} onClose={() => setResetUser(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        <Box sx={{
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff', px: 2.5, py: 2,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 40, height: 40, color: '#fff' }}>
            <ResetIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800}>Reset Password</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              {resetUser?.first_name} {resetUser?.last_name} · {resetUser?.email}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setResetUser(null)} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 2.5 }}>
          <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>
            The user will be asked to <strong>change this password</strong> on their next login.
          </Alert>
          <TextField fullWidth size="small" label="New Temporary Password"
            type={showResetPw ? 'text' : 'password'}
            value={resetPw} onChange={(e) => setResetPw(e.target.value)}
            helperText="Minimum 6 characters"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><LockIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Generate strong password">
                    <IconButton size="small" onClick={() => setResetPw(genPassword())}>
                      <DiceIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={showResetPw ? 'Hide' : 'Show'}>
                    <IconButton size="small" onClick={() => setShowResetPw(s => !s)}>
                      {showResetPw ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }} />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setResetUser(null)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={submitReset}
            startIcon={<ResetIcon fontSize="small" />}
            sx={{ fontWeight: 700 }}>
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
